import {useEffect} from 'react';

interface SeoOptions {
    title: string;
    description?: string;
    canonicalPath?: string;
    keywords?: string;
    ogImage?: string;
    ogType?: 'website' | 'article' | 'profile';
    robots?: string;
    /**
     * JSON-LD structured data attached to the page. Pass an array of objects
     * (each becomes a separate <script type="application/ld+json"> tag).
     */
    jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
    /**
     * Breadcrumb items to emit as BreadcrumbList JSON-LD.
     */
    breadcrumbs?: Array<{name: string; path: string}>;
}

const BASE_URL = 'https://kamerymielec.pl';
const DEFAULT_OG_IMAGE = `${BASE_URL}/meta.webp`;
const SEO_DATA_ATTR = 'data-seo-ld';

function setMeta(attr: 'name' | 'property', key: string, content: string) {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function setCanonical(href: string) {
    let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

function setOgUrl(href: string) {
    setMeta('property', 'og:url', href);
}

function clearDynamicJsonLd() {
    document.head
        .querySelectorAll(`script[type="application/ld+json"][${SEO_DATA_ATTR}]`)
        .forEach((node) => node.parentElement?.removeChild(node));
}

function addJsonLd(data: Record<string, unknown>) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(SEO_DATA_ATTR, 'true');
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
}

/**
 * Lightweight client-side SEO helper for this CRA SPA (no SSR/helmet).
 * Updates title, description, canonical, Open Graph, Twitter, robots and
 * dynamic JSON-LD per route.
 */
export function useSeo({
    title,
    description,
    canonicalPath,
    keywords,
    ogImage,
    ogType = 'website',
    robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    jsonLd,
    breadcrumbs,
}: SeoOptions) {
    useEffect(() => {
        document.title = title;
        const path = canonicalPath ?? window.location.pathname;
        const url = `${BASE_URL}${path}`;
        const image = ogImage ?? DEFAULT_OG_IMAGE;

        if (description) {
            setMeta('name', 'description', description);
            setMeta('property', 'og:description', description);
            setMeta('name', 'twitter:description', description);
        }
        if (keywords) {
            setMeta('name', 'keywords', keywords);
        }

        setMeta('property', 'og:title', title);
        setMeta('property', 'og:type', ogType);
        setMeta('property', 'og:image', image);
        setMeta('property', 'og:locale', 'pl_PL');
        setMeta('property', 'og:site_name', 'Kamery Mielec - ONEXO');
        setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', title);
        setMeta('name', 'twitter:image', image);
        setMeta('name', 'robots', robots);
        setMeta('name', 'googlebot', robots);

        setCanonical(url);
        setOgUrl(url);

        // Dynamic JSON-LD per page (does not touch the static blocks in index.html).
        clearDynamicJsonLd();
        const blocks: Array<Record<string, unknown>> = [];
        if (Array.isArray(jsonLd)) blocks.push(...jsonLd);
        else if (jsonLd) blocks.push(jsonLd);

        if (breadcrumbs && breadcrumbs.length > 0) {
            blocks.push({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbs.map((b, i) => ({
                    '@type': 'ListItem',
                    position: i + 1,
                    name: b.name,
                    item: `${BASE_URL}${b.path}`,
                })),
            });
        }

        blocks.forEach(addJsonLd);

        return () => {
            clearDynamicJsonLd();
        };
    }, [title, description, canonicalPath, keywords, ogImage, ogType, robots, jsonLd, breadcrumbs]);
}
